import { ICustomPageEmbeddedExportRequestsData, IDataset, JsonObject } from '@se/cp.common';
import {
    createValidatedObjectAtPath,
    findPathByKeyValue,
    getNodeAtPath,
    mergeObjectAtPath,
    normalizePatchByPath,
    pushObjectAtArrayPath,
} from '@se/se.base.utils';
import { ConstraintTypeEnum, IConstraint } from '@se/se.platform.base';

/**
 * filtraggio regole disattivate basato su regola di tipo Disabler. (task 75339-75338)
 * @param constraints
 */
export const filterConstraintsByDisabler = (constraints: IConstraint[]): IConstraint[] => {
    const ids = new Set(
        constraints
            .filter((constraint) => constraint.constraintType === ConstraintTypeEnum.Disabler)
            .map((constraint) => [constraint.id, constraint.constraintIdRef])
            .flat(),
    );
    if (!ids.size) return constraints;
    return constraints.filter((constraint) => !ids.has(constraint.id));
};

const STUDY_PATH = 'ResearchArchive.0.JSonData.Studies.ItemsStudy';
const joinPath = (base: string, rel?: string): string => (rel ? `${base}.${rel}` : base);

export const mergeDataForResearchArchive = (
    model: JsonObject,
    dataset: IDataset,
    exportRequests: ICustomPageEmbeddedExportRequestsData[],
): JsonObject => {
    let mergedModel: JsonObject = model;
    for (const req of exportRequests) {
        // -------- STUDY --------
        let studyPath: string = STUDY_PATH;
        let studiesArr = getNodeAtPath(mergedModel, studyPath);
        if (studiesArr !== undefined && !Array.isArray(studiesArr)) {
            console.error('ItemsStudies is not an array: cannot merge');
            return model;
        }

        let relStudyPath = findPathByKeyValue(studiesArr, 'StudyInstanceUID', req.studyInstanceUID);
        if (relStudyPath !== undefined) {
            studyPath = joinPath(studyPath, relStudyPath);
        } else {
            /** se non trovo lo studio ne creo uno nuovo */
            const newStudy = createValidatedObjectAtPath(dataset, studyPath, {
                StudyInstanceUID: req.studyInstanceUID,
            });
            if (!newStudy) {
                console.error('error creating study: cannot merge');
                return model;
            }

            mergedModel = pushObjectAtArrayPath(mergedModel, studyPath, newStudy);

            studiesArr = getNodeAtPath(mergedModel, studyPath);
            relStudyPath = Array.isArray(studiesArr)
                ? findPathByKeyValue(studiesArr, 'StudyInstanceUID', req.studyInstanceUID)
                : undefined;

            if (relStudyPath === undefined) {
                console.error('error adding study: cannot merge');
                return model;
            }

            studyPath = joinPath(studyPath, relStudyPath);
        }
        // -------- SERIES --------
        let seriesPath: string = `${studyPath}.ItemsSeries`;
        let seriesArr = getNodeAtPath(mergedModel, seriesPath);
        if (seriesArr !== undefined && !Array.isArray(seriesArr)) {
            console.error('ItemsSeries is not an array: cannot merge');
            return model;
        }
        let relSeriesPath = findPathByKeyValue(seriesArr, 'SeriesInstanceUID', req.seriesInstanceUID);

        if (relSeriesPath !== undefined) {
            seriesPath = joinPath(seriesPath, relSeriesPath);
        } else {
            const newSeries = createValidatedObjectAtPath(dataset, seriesPath, {
                SeriesInstanceUID: req.seriesInstanceUID,
            });
            if (!newSeries) {
                console.error('error creating series: cannot merge');
                return model;
            }

            mergedModel = pushObjectAtArrayPath(mergedModel, seriesPath, newSeries);

            seriesArr = getNodeAtPath(mergedModel, seriesPath);
            relSeriesPath = Array.isArray(seriesArr)
                ? findPathByKeyValue(seriesArr, 'SeriesInstanceUID', req.seriesInstanceUID)
                : undefined;

            if (relSeriesPath === undefined) {
                console.error('error adding series: cannot merge');
                return model;
            }

            seriesPath = joinPath(seriesPath, relSeriesPath);
        }
        // -------- INSTANCE --------
        let instancePath: string = `${seriesPath}.ItemsInstances`;
        let instancesArr = getNodeAtPath(mergedModel, instancePath);
        if (instancesArr !== undefined && !Array.isArray(instancesArr)) {
            console.error('ItemsInstances is not an array: cannot merge');
            return model;
        }

        let relInstancePath = findPathByKeyValue(instancesArr, 'SOPInstanceUID', req.sopInstanceUID);

        if (relInstancePath !== undefined) {
            instancePath = joinPath(instancePath, relInstancePath);

            // PATCH (merge shallow) con validazione parziale dataset-aware
            const patch = normalizePatchByPath(dataset, instancePath, {
                SOPInstanceUID: req.sopInstanceUID,
                WADOUrl: req.retrieveUrl,
                RepositoryFilePath: req.repositoryFilePath,
            });

            if (!patch) {
                console.error('error creating patch: cannot merge');
                return model;
            }

            mergedModel = mergeObjectAtPath(mergedModel, instancePath, patch);
        } else {
            const newInstance = createValidatedObjectAtPath(dataset, instancePath, {
                SOPInstanceUID: req.sopInstanceUID,
                WADOUrl: req.retrieveUrl,
                RepositoryFilePath: req.repositoryFilePath,
            });

            if (!newInstance) {
                console.error('error creating item instance: cannot merge');
                return model;
            }

            mergedModel = pushObjectAtArrayPath(mergedModel, instancePath, newInstance);

            instancesArr = getNodeAtPath(mergedModel, instancePath);
            relInstancePath = Array.isArray(instancesArr)
                ? findPathByKeyValue(instancesArr, 'SOPInstanceUID', req.sopInstanceUID)
                : undefined;

            if (relInstancePath === undefined) {
                console.error('error adding instance: cannot merge');
                return model;
            }

            instancePath = joinPath(instancePath, relInstancePath);
        }
    }

    return mergedModel;
};
